import { useCallback, useMemo, useState } from 'react';

const debounce = (func: (...args: any[]) => void, delay: number) => {
  let timeoutId: ReturnType<typeof setTimeout>;
  return function (this: any, ...args: any[]) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func.apply(this, args);
    }, delay);
  };
};

/**
 * Custom hook to manage the state of an input field, providing both
 * the immediate display value and a debounced value for delayed processing.
 * * @param defaultValue The initial value of the input.
 * @param debounceDelay The delay (in milliseconds) before the debounced value updates.
 * @returns An object containing the immediate value, debounced value, and handlers.
 */
export const useInputWithDebounce = (
  defaultValue: string,
  debounceDelay: number = 500,
  validator: (value: string) => boolean = () => true,
) => {
  const [inputValue, setInputValue] = useState(defaultValue);
  const [debouncedValue, setDebouncedValue] = useState(defaultValue);
  const [isValid, setIsValid] = useState(validator(defaultValue));
  const [didChange, setDidChange] = useState(false);

  const validate = useCallback(
    (value: string) => {
      return validator(value);
    },
    [validator],
  );

  const debouncedSetDebouncedValue = useMemo(
    () =>
      debounce((nextValue: string) => {
        setDebouncedValue(nextValue);
      }, debounceDelay),
    [debounceDelay],
  );

  const handleDebouncedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    setInputValue(nextValue);
    setIsValid(validate(nextValue));
    setDidChange(true);
    debouncedSetDebouncedValue(nextValue);
  };

  const setValue = (v: string) => {
    setInputValue(v);
    setDebouncedValue(v);
    setIsValid(validate(v));
  };

  const clearInput = () => {
    setValue('');
  };

  return {
    value: inputValue,
    debouncedValue,
    isValid,
    didChange,
    handleChange: handleDebouncedChange,
    setValue,
    clearInput,
  };
};
